import React, { useState, useRef, useCallback } from 'react';
import {
  IconButton,
  Tooltip,
  CircularProgress,
  Box,
  Typography,
  Fade,
} from '@mui/material';
import {
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Stop as StopIcon,
} from '@mui/icons-material';

interface VoiceCommandButtonProps {
  onVoiceCommand: (transcript: string) => void;
  isProcessing?: boolean;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
}

interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  serviceURI: string;
  grammars: any;
  start(): void;
  stop(): void;
  abort(): void;
  onaudiostart: ((this: SpeechRecognition, ev: Event) => any) | null;
  onaudioend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onerror: ((this: SpeechRecognition, ev: SpeechRecognitionErrorEvent) => any) | null;
  onnomatch: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => any) | null;
  onresult: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => any) | null;
  onsoundstart: ((this: SpeechRecognition, ev: Event) => any) | null;
  onsoundend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onspeechstart: ((this: SpeechRecognition, ev: Event) => any) | null;
  onspeechend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onstart: ((this: SpeechRecognition, ev: Event) => any) | null;
}

const VoiceCommandButton: React.FC<VoiceCommandButtonProps> = ({
  onVoiceCommand,
  isProcessing = false,
  disabled = false,
  size = 'medium',
}) => {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Accumulates finalized speech across pauses; submitted as one message when
  // the user stops (so a thinking pause never auto-sends). submittedRef guards
  // against a double-submit from overlapping stop/end events.
  const finalTranscriptRef = useRef('');
  const submittedRef = useRef(false);

  // Initialize speech recognition
  const initializeSpeechRecognition = useCallback(() => {
    if (!window.SpeechRecognition && !window.webkitSpeechRecognition) {
      setIsSupported(false);
      return null;
    }

    const SpeechRecognitionClass = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognitionClass();

    // Continuous so a pause to think doesn't end the session; we accumulate
    // and submit only when the user stops (or the 30s cap fires).
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
      setError(null);
      setTranscript('');
      finalTranscriptRef.current = '';
      submittedRef.current = false;
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          // Accumulate finalized speech; do NOT submit on a pause — the user
          // decides when to send by clicking the mic again.
          finalTranscriptRef.current += result[0].transcript + ' ';
        } else {
          interimTranscript += result[0].transcript;
        }
      }

      // Show the running transcript (accumulated final + live interim).
      setTranscript((finalTranscriptRef.current + interimTranscript).trim());
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      setIsListening(false);
      setTranscript('');
      
      let errorMessage = 'Voice recognition failed';
      switch (event.error) {
        case 'no-speech':
          errorMessage = 'No speech detected. Please try again.';
          break;
        case 'audio-capture':
          errorMessage = 'Microphone not accessible. Please check permissions.';
          break;
        case 'not-allowed':
          errorMessage = 'Microphone permission denied. Please enable microphone access.';
          break;
        case 'network':
          errorMessage = 'Network error. Please check your internet connection.';
          break;
        case 'aborted':
          errorMessage = 'Voice recognition was cancelled.';
          break;
        case 'bad-grammar':
          errorMessage = 'Speech recognition grammar error.';
          break;
        case 'language-not-supported':
          errorMessage = 'Language not supported.';
          break;
        default:
          errorMessage = `Voice recognition error: ${event.error}`;
      }
      
      setError(errorMessage);
      
      // Clear error after 5 seconds
      setTimeout(() => setError(null), 5000);
    };

    recognition.onend = () => {
      setIsListening(false);

      // Recognition ends when the user clicks stop, the 30s cap fires, or the
      // browser closes the session — in every case, send everything captured
      // this session exactly once.
      const text = finalTranscriptRef.current.trim();
      if (text && !error && !submittedRef.current) {
        submittedRef.current = true;
        onVoiceCommand(text);
      }
      finalTranscriptRef.current = '';
      setTranscript('');
    };

    return recognition;
  }, [onVoiceCommand, error]);

  const startListening = useCallback(() => {
    if (!isSupported) {
      setError('Speech recognition is not supported in this browser');
      return;
    }

    if (isListening || isProcessing || disabled) return;

    const recognition = initializeSpeechRecognition();
    if (!recognition) return;

    recognitionRef.current = recognition;

    try {
      recognition.start();
      
      // Set a timeout to stop listening after 30 seconds
      timeoutRef.current = setTimeout(() => {
        stopListening();
      }, 30000);
    } catch (err) {
      setError('Failed to start voice recognition');
      setIsListening(false);
    }
  }, [isSupported, isListening, isProcessing, disabled, initializeSpeechRecognition]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    setIsListening(false);
  }, []);

  const handleClick = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  if (!isSupported) {
    return (
      <Tooltip title="Voice commands are not supported in this browser">
        <span>
          <IconButton disabled size={size}>
            <MicOffIcon />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  const getIcon = () => {
    if (isProcessing) {
      return <CircularProgress size={size === 'small' ? 16 : size === 'large' ? 24 : 20} />;
    }
    if (isListening) {
      return <StopIcon color="error" />;
    }
    return <MicIcon />;
  };

  const getTooltipText = () => {
    if (isProcessing) return 'Processing voice command...';
    if (isListening) return 'Listening — click to stop & send';
    if (error) return error;
    return 'Click to speak (pause to think; click again to send)';
  };

  const buttonColor = isListening ? 'error' : 'primary';

  return (
    <Box sx={{ position: 'relative' }}>
      <Tooltip title={getTooltipText()}>
        <span>
          <IconButton
            onClick={handleClick}
            disabled={disabled || isProcessing}
            color={buttonColor}
            size={size}
            sx={{
              animation: isListening ? 'pulse 1.5s ease-in-out infinite' : 'none',
              '@keyframes pulse': {
                '0%': {
                  transform: 'scale(1)',
                  opacity: 1,
                },
                '50%': {
                  transform: 'scale(1.1)',
                  opacity: 0.7,
                },
                '100%': {
                  transform: 'scale(1)',
                  opacity: 1,
                },
              },
            }}
          >
            {getIcon()}
          </IconButton>
        </span>
      </Tooltip>
      
      {/* Show transcript while listening */}
      <Fade in={isListening && !!transcript}>
        <Box
          sx={{
            position: 'absolute',
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            mb: 1,
            px: 2,
            py: 1,
            backgroundColor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            boxShadow: 2,
            maxWidth: 200,
            zIndex: 1000,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Listening...
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {transcript}
          </Typography>
        </Box>
      </Fade>
    </Box>
  );
};

export default VoiceCommandButton;